import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, fmtErr, fmtDate, REVIEW_SERVICES } from "../lib/api";
import { ORACOLI_MENU } from "../lib/methods";
import { useAuth } from "../context/AuthContext";
import { StatusBadge } from "../components/StatusBadge";
import ProfileEdit from "../components/ProfileEdit";
import { PasswordInput } from "../components/PasswordInput";
import { toast } from "sonner";
import { ShieldAlert, MessageCircleHeart, Star, Trash2, Heart } from "lucide-react";

const METODO_LABELS = Object.fromEntries(ORACOLI_MENU.map((o) => [o.to.slice(1), o.label]));

export default function AreaCliente() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [showDelete, setShowDelete] = useState(false);
  const [delPassword, setDelPassword] = useState("");
  const [delBusy, setDelBusy] = useState(false);
  const [ent, setEnt] = useState(null);
  const [vqList, setVqList] = useState([]);
  const [reviewService, setReviewService] = useState("sibille");
  const [reviewRating, setReviewRating] = useState(5);
  const [reviewText, setReviewText] = useState("");
  const [reviewBusy, setReviewBusy] = useState(false);
  const [reviewSent, setReviewSent] = useState(false);
  const [metodi, setMetodi] = useState(null);

  useEffect(() => {
    api.get("/metodi/uso").then((r) => setMetodi(r.data)).catch(() => setMetodi({ favorites: [], usage: {} }));
  }, []);

  const submitReview = async () => {
    setReviewBusy(true);
    try {
      await api.post("/reviews", { service: reviewService, text: reviewText, rating: reviewRating });
      setReviewSent(true);
      toast.success("Grazie per la tua recensione!");
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setReviewBusy(false);
    }
  };
  const [vqText, setVqText] = useState("");
  const [vqBusy, setVqBusy] = useState(false);
  const [vqReplies, setVqReplies] = useState({});

  const load = useCallback(() => {
    api.get("/entitlements").then((r) => setEnt(r.data)).catch(() => {});
    api.get("/virginia/mine").then((r) => setVqList(r.data)).catch(() => {});
    api.get("/reviews/mine").then((r) => { if (r.data.reviewed) setReviewSent(true); }).catch(() => {});
  }, []);

  const virginiaRef = useRef(null);
  useEffect(() => {
    const el = virginiaRef.current;
    if (!el) return;
    const obs = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) {
        api.post("/virginia/seen").then(() => window.dispatchEvent(new Event("arcana-virginia-seen"))).catch(() => {});
        obs.disconnect();
      }
    }, { threshold: 0.2 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  useEffect(() => load(), [load]);

  const deleteAccount = async (e) => {
    e.preventDefault();
    setDelBusy(true);
    try {
      await api.delete("/auth/account", { data: { password: delPassword } });
      toast.success("Account e dati eliminati definitivamente.");
      window.location.href = "/";
    } catch (err) {
      toast.error(fmtErr(err, "Eliminazione non riuscita."));
      setDelBusy(false);
    }
  };

  const sendVirginiaQuestion = async () => {
    setVqBusy(true);
    try {
      await api.post("/virginia/question", { text: vqText.trim() });
      toast.success("Domanda inviata a Virginia. Ti risponderà personalmente.");
      setVqText("");
      load();
    } catch (e) {
      toast.error(fmtErr(e, "Invio non riuscito."));
    } finally {
      setVqBusy(false);
    }
  };

  const deleteVirginia = async (id) => {
    if (!window.confirm("Vuoi eliminare definitivamente questa conversazione con Virginia? L'operazione è definitiva e non ripristina la domanda del periodo.")) return;
    try {
      await api.delete(`/virginia/${id}`);
      toast.success("Conversazione eliminata.");
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const sendVirginiaReply = async (id) => {
    const text = (vqReplies[id] || "").trim();
    if (text.length < 2) return;
    try {
      await api.post(`/virginia/${id}/reply`, { text });
      toast.success("Replica inviata. Virginia ti risponderà un'ultima volta.");
      setVqReplies({ ...vqReplies, [id]: "" });
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 lg:px-8 py-16 sm:py-20" data-testid="area-cliente-page">
      <div className="mb-12">
        <p className="eyebrow">Area Cliente</p>
        <h1 className="font-display text-4xl text-amber-100 mt-3">Ciao, {user?.name}</h1>
        <p className="text-purple-200/70 mt-2 text-sm">Qui trovi il tuo profilo e il riepilogo della tua attività.</p>
      </div>

      <ProfileEdit />

      {user?.role === "admin" && (
        <div className="mystic-card p-6 mb-10 flex flex-col sm:flex-row sm:items-center gap-4" data-testid="cliente-admin-posts-card">
          <div className="flex-1">
            <h2 className="font-cinzel text-xs tracking-[0.25em] uppercase text-amber-400">Oltre le Carte — gestione contenuti</h2>
            <p className="text-xs text-purple-300/60 mt-1.5">Visibile solo al tuo account amministratore. Crea, modifica ed elimina i post pubblici.</p>
          </div>
          <Link to="/admin?tab=oltre&new=1" data-testid="cliente-new-post-btn" className="gold-btn inline-block px-6 py-2.5 text-xs text-center shrink-0">
            Scrivi un nuovo post
          </Link>
        </div>
      )}

      <div className="mt-14" data-testid="cliente-metodi-section">
        <h2 className="font-cinzel text-xs tracking-[0.25em] uppercase text-amber-400/80 flex items-center gap-2 mb-2">
          <Heart className="w-4 h-4" /> I tuoi metodi
        </h2>
        <div className="mystic-card p-6" data-testid="cliente-metodi-card">
          {!metodi ? (
            <p className="text-xs text-purple-300/50">Caricamento…</p>
          ) : (
            <div className="space-y-5">
              <div>
                <p className="text-[10px] uppercase tracking-[0.25em] text-amber-400/70 mb-2">Metodi del cuore</p>
                {metodi.favorites.length > 0 ? (
                  <div className="flex flex-wrap gap-2" data-testid="cliente-metodi-favorites">
                    {metodi.favorites.map((k) => (
                      <Link key={k} to={`/${k}`} data-testid={`cliente-metodo-fav-${k}`}
                        className="inline-flex items-center gap-1.5 text-[11px] rounded-full border border-rose-300/40 text-rose-200 px-3 py-1.5 hover:bg-rose-400/10 transition-colors">
                        <Heart className="w-3 h-3 fill-rose-300 text-rose-300" /> {METODO_LABELS[k] || k}
                      </Link>
                    ))}
                  </div>
                ) : (
                  <p className="text-[11px] text-purple-300/50" data-testid="cliente-metodi-no-favorites">
                    Nessuno ancora: tocca il cuoricino sulla scheda di un metodo per aggiungerlo qui.
                  </p>
                )}
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-[0.25em] text-amber-400/70 mb-2">Metodi che hai utilizzato</p>
                {Object.keys(metodi.usage).length > 0 ? (
                  <div className="space-y-1.5" data-testid="cliente-metodi-usage">
                    {Object.entries(metodi.usage).sort((a, b) => b[1] - a[1]).map(([k, n]) => (
                      <div key={k} className="flex items-center justify-between text-[11px]" data-testid={`cliente-metodo-uso-${k}`}>
                        <span className="text-purple-100/80">{METODO_LABELS[k] || k}</span>
                        <span className="text-purple-300/60">{n} {n === 1 ? "consultazione" : "consultazioni"}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-[11px] text-purple-300/50" data-testid="cliente-metodi-no-usage">Le tue consultazioni appariranno qui.</p>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="mt-14" data-testid="cliente-review-section">
        <h2 className="font-cinzel text-xs tracking-[0.25em] uppercase text-amber-400/80 flex items-center gap-2 mb-2">
          <Star className="w-4 h-4" /> Lascia una recensione
        </h2>
        <p className="text-xs text-purple-300/60 mb-5">
          Hai provato un servizio? Racconta la tua esperienza.
        </p>
        {!reviewSent && (
          <div className="mystic-card p-6 space-y-4" data-testid="review-form-user">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <select
                value={reviewService}
                onChange={(e) => setReviewService(e.target.value)}
                className="input-dark"
                data-testid="review-service-select"
              >
                {REVIEW_SERVICES.map((s) => <option key={s.v} value={s.v}>{s.label}</option>)}
              </select>
              <div className="flex items-center gap-1.5" data-testid="review-stars">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button key={n} type="button" onClick={() => setReviewRating(n)} data-testid={`review-star-${n}`} aria-label={`${n} stelle`} className="transition-transform hover:scale-110">
                    <Star className={`w-6 h-6 ${n <= reviewRating ? "text-amber-400 fill-amber-400" : "text-purple-300/30"}`} />
                  </button>
                ))}
              </div>
            </div>
            <textarea
              value={reviewText}
              onChange={(e) => setReviewText(e.target.value)}
              rows={3}
              maxLength={1000}
              placeholder="La tua esperienza, con parole tue…"
              className="input-dark"
              data-testid="review-text-user"
            />
            <div className="flex justify-end">
              <button
                onClick={submitReview}
                disabled={reviewBusy || reviewText.trim().length < 5}
                data-testid="review-submit-btn"
                className="gold-btn px-6 py-2 text-xs disabled:opacity-50"
              >
                {reviewBusy ? "Invio…" : "Invia la recensione"}
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="mt-14" data-testid="cliente-virginia-section" ref={virginiaRef}>
        <h2 className="font-cinzel text-xs tracking-[0.25em] uppercase text-amber-400/80 flex items-center gap-2 mb-2">
          <MessageCircleHeart className="w-4 h-4" /> 💌 Scrivi a Virginia
        </h2>
        {ent && (ent.plan === "sub" || ent.plan === "admin") && (
          <p className="text-xs text-purple-300/60 mb-5" data-testid="virginia-monthly-status">
            Domanda personale di questo periodo:{" "}
            {ent.virginia_question_available
              ? <span className="text-emerald-300">disponibile</span>
              : <span className="text-amber-300/80">già utilizzata — tornerà disponibile al prossimo rinnovo</span>}
          </p>
        )}
        {ent && ent.plan !== "sub" && ent.plan !== "admin" ? (
          <div className="mystic-card p-6 text-center" data-testid="virginia-upsell">
            <p className="text-sm text-purple-200/75 leading-relaxed">
              Con l'abbonamento hai ogni mese una domanda personale: scrivi direttamente a Virginia
              e ti risponderà lei, personalmente.
            </p>
            <Link to="/piani" data-testid="virginia-upsell-link" className="gold-btn inline-block px-6 py-2.5 text-xs mt-4">
              Scopri l'abbonamento
            </Link>
          </div>
        ) : (
          ent?.virginia_question_available && (
            <div className="mystic-card p-6" data-testid="virginia-form">
              <p className="text-sm text-purple-100/85 mb-1">Hai una domanda personale disponibile per questo periodo.</p>
              <p className="text-xs text-purple-300/60 mb-4 leading-relaxed">
                Scrivi direttamente a Virginia: ti risponderà personalmente, e potrai replicare una volta alla sua risposta.
              </p>
              <textarea
                value={vqText}
                onChange={(e) => setVqText(e.target.value)}
                rows={4}
                maxLength={1000}
                placeholder="Scrivi qui la tua domanda per Virginia…"
                data-testid="virginia-question-input"
                className="input-dark mb-3"
              />
              <button
                onClick={sendVirginiaQuestion}
                disabled={vqBusy || vqText.trim().length < 5}
                data-testid="virginia-question-btn"
                className="gold-btn px-5 py-2 text-xs disabled:opacity-50"
              >
                {vqBusy ? "Invio…" : "Invia a Virginia"}
              </button>
            </div>
          )
        )}
        <div className="space-y-4">
            {vqList.map((q) => (
              <div key={q.id} className="mystic-card p-5" data-testid={`virginia-item-${q.id}`}>
                <div className="flex items-center justify-between mb-3">
                  <p className="text-xs text-purple-300/50">{fmtDate(q.created_at)}</p>
                  <div className="flex items-center gap-3">
                    <span className="text-[10px] uppercase tracking-wider border border-amber-500/30 text-amber-300/80 rounded-full px-3 py-1">
                      {{ pending: "In attesa di risposta", answered: "Risposta ricevuta · puoi replicare una volta", user_replied: "In attesa della risposta finale", closed: "Conversazione conclusa" }[q.status]}
                    </span>
                    <button onClick={() => deleteVirginia(q.id)} data-testid={`virginia-delete-${q.id}`} className="flex items-center gap-1 text-[11px] text-red-300/70 hover:text-red-300 transition-colors">
                      <Trash2 className="w-3.5 h-3.5" /> Elimina
                    </button>
                  </div>
                </div>
                {q.messages.map((m, i) => (
                  <div key={i} className={m.role === "virginia"
                    ? "rounded-lg bg-amber-500/10 border border-amber-500/25 px-4 py-3 mb-2"
                    : "rounded-lg bg-[#0f0617] border border-[#4a2175] px-4 py-3 mb-2"}>
                    <p className="text-[10px] uppercase tracking-wider text-purple-300/50 mb-1">{m.role === "virginia" ? "Virginia" : "Tu"}</p>
                    <p className="text-sm text-purple-100/85 whitespace-pre-line">{m.text}</p>
                  </div>
                ))}
                {q.status === "answered" && (
                  <div className="mt-3 space-y-3">
                    <textarea
                      value={vqReplies[q.id] || ""}
                      onChange={(e) => setVqReplies({ ...vqReplies, [q.id]: e.target.value })}
                      rows={3}
                      maxLength={1000}
                      placeholder="Puoi replicare una volta alla risposta di Virginia…"
                      data-testid={`virginia-reply-input-${q.id}`}
                      className="input-dark"
                    />
                    <button
                      onClick={() => sendVirginiaReply(q.id)}
                      data-testid={`virginia-reply-btn-${q.id}`}
                      className="text-xs text-amber-200 border border-amber-500/40 rounded-full px-4 py-2 hover:bg-amber-500/10 transition-colors"
                    >
                      Invia replica (ultima)
                    </button>
                  </div>
                )}
              </div>
            ))}
        </div>
      </div>

      <div className="mt-16 pt-10 border-t border-red-500/20" data-testid="cliente-danger-zone">
        <h2 className="font-cinzel text-xs tracking-[0.25em] uppercase text-red-300/80 flex items-center gap-2 mb-4">
          <ShieldAlert className="w-4 h-4" /> Privacy e dati
        </h2>
        {!showDelete ? (
          <button
            onClick={() => setShowDelete(true)}
            data-testid="account-delete-open-btn"
            className="text-xs text-red-300/80 hover:text-red-300 border border-red-500/40 rounded-full px-5 py-2 transition-colors"
          >
            Elimina il mio account e tutti i dati
          </button>
        ) : (
          <form onSubmit={deleteAccount} className="mystic-card p-6" data-testid="account-delete-form">
            <p className="text-sm text-purple-100/80 leading-relaxed mb-4">
              L'eliminazione è <span className="text-red-300 font-medium">definitiva</span>: verranno cancellati account, letture, geomanzie, prenotazioni e la tua memoria privata. Per confermare, inserisci la tua password.
            </p>
            <div className="mb-4">
              <PasswordInput
                required
                value={delPassword}
                onChange={(e) => setDelPassword(e.target.value)}
                placeholder="La tua password"
                data-testid="account-delete-password"
                className="input-dark"
              />
            </div>
            <div className="flex items-center gap-4">
              <button
                type="submit"
                disabled={delBusy}
                data-testid="account-delete-confirm-btn"
                className="text-xs bg-red-500/20 hover:bg-red-500/30 text-red-200 border border-red-500/50 rounded-full px-5 py-2 transition-colors disabled:opacity-60"
              >
                {delBusy ? "Eliminazione…" : "Elimina definitivamente"}
              </button>
              <button
                type="button"
                onClick={() => { setShowDelete(false); setDelPassword(""); }}
                data-testid="account-delete-cancel-btn"
                className="text-xs text-purple-300/70 hover:text-purple-200 px-3 py-2"
              >
                Annulla
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
